import type React from "react";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Tabs } from "@/design-system/Tabs";
import { useBool } from "@/hooks/useBool";
import { useTranslation } from "@/translations";

interface WizardStepProps {
  id: number;
  title: string;
  children: React.ReactNode;
  canLeave: () => boolean | Promise<boolean>;
  hasFinish: boolean;
  isHidden?: boolean;
}

export interface WizardProps {
  children: React.ReactElement<WizardStepProps>[];
  strictOrder: boolean;
  isSubmitting: boolean;
  onFinish: () => void;
  actionsText: {
    prevStep?: string;
    nextStep?: string;
    finish?: string;
  };
  initialStepId?: WizardStepProps["id"];
}

export function WizardStep(props: WizardStepProps): React.ReactNode {
  return props.children;
}

export function Wizard({
  children,
  actionsText,
  strictOrder,
  isSubmitting,
  initialStepId,
  onFinish,
}: WizardProps): React.ReactNode {
  const { t } = useTranslation();

  const tabs = children
    .filter((x) => !x.props.isHidden)
    .map((step) => ({
      id: step.props.id,
      title: step.props.title,
      content: step.props.children,
      canLeave: step.props.canLeave,
      hasFinish: step.props.hasFinish,
    }));

  const [currentStepId, setCurrentStepId] = useState(() => initialStepId ?? tabs[0].id);
  const [maxVisitedStepId, setMaxVisitedStepId] = useState(currentStepId);
  const [isCheckingCanLeave, isCheckingCanLeaveHandlers] = useBool();

  const currentTabIndex = tabs.findIndex((tab) => tab.id === currentStepId);
  const previousTab = tabs[currentTabIndex - 1];
  const currentTab = tabs[currentTabIndex];
  const nextTab = tabs[currentTabIndex + 1];

  function isTabDisabled(stepId: number) {
    if (stepId === currentStepId) {
      return false;
    }

    if (!strictOrder) {
      return false;
    }

    if (maxVisitedStepId > stepId) {
      return false;
    }

    return currentStepId < stepId;
  }

  async function switchTab(tab: (typeof tabs)[number] | undefined) {
    if (!tab) {
      return;
    }

    // If we are going back, we don't need to check if we can leave
    if (tab.id < currentTab.id) {
      setCurrentStepId(tab.id);
      setMaxVisitedStepId(Math.max(maxVisitedStepId, tab.id));
    } else {
      // If we are going forward, we need to check if we can leave the current tab, not the destination one
      const currentTab = tabs.find(({ id }) => id === currentStepId);
      const canLeave = await currentTab!.canLeave?.();
      if (canLeave) {
        setCurrentStepId(tab.id);
        setMaxVisitedStepId(Math.max(maxVisitedStepId, tab.id));
      }
    }
  }

  const lastStepId = tabs[tabs.length - 1].id;
  const isFinishButtonShown = currentTab.hasFinish || currentStepId === lastStepId;

  return (
    <section className="flex flex-1 flex-col" data-testid="wizard">
      {tabs.length > 1 ? (
        <Tabs.Root
          tabs={tabs.map((tab, index) => ({
            label: `${index + 1}. ${tab.title}`,
            value: tab.id.toString(),
            isDisabled: isTabDisabled(tab.id),
          }))}
          activeTab={currentStepId.toString()}
          onSelectTab={(tabId) => switchTab(tabs.find((tab) => tab.id.toString() === tabId))}
          variant="inline"
        >
          {tabs.map((x) => (
            <Tabs.Content key={x.id} value={x.id.toString()}>
              {x.content}
            </Tabs.Content>
          ))}
        </Tabs.Root>
      ) : (
        tabs[0]?.content
      )}

      <footer className="mt-auto flex w-full flex-wrap justify-between pt-space-xl">
        {previousTab && (
          <Button
            data-testid="prev-step"
            disabled={isCheckingCanLeave || isSubmitting}
            variant="soft"
            onClick={() => switchTab(previousTab)}
          >
            {actionsText.prevStep || t("common.action.back")}
          </Button>
        )}
        <nav className="ml-auto flex flex-wrap gap-space-sm">
          {nextTab && (
            <Button
              data-testid="next-step"
              variant={isFinishButtonShown ? "soft" : "filled"}
              disabled={isCheckingCanLeave || isSubmitting}
              onClick={() => switchTab(nextTab)}
            >
              {actionsText.nextStep || t("common.action.next")}
            </Button>
          )}
          {isFinishButtonShown && (
            <Button
              data-testid="finish-step"
              isLoading={isCheckingCanLeave || isSubmitting}
              onClick={async () => {
                try {
                  isCheckingCanLeaveHandlers.setTrue();

                  const canLeave = await currentTab.canLeave();
                  if (canLeave) {
                    onFinish();
                  }
                } finally {
                  isCheckingCanLeaveHandlers.setFalse();
                }
              }}
            >
              {actionsText.finish || t("component.wizard.action.finish")}
            </Button>
          )}
        </nav>
      </footer>
    </section>
  );
}
